import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Trophy, ArrowLeft } from '@phosphor-icons/react';
import SEO from '../components/SEO';
import {
  TournamentBasicsSection,
  TournamentLocationSection,
  TournamentScheduleSection,
  TournamentMediaSection,
  TournamentContentSection,
  TournamentPaymentSection
} from '../components/tournament/TournamentFormSections';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CreateTournament = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [f, setF] = useState({
    name: '', format: '5v5', city: '', state: '', venue: '',
    registration_opens_at: '', registration_closes_at: '', starts_at: '', ends_at: '',
    age_categories: [], gender_categories: [], rules: '', prizes: '',
    registration_fee: 0, fee_mode: 'single', category_fees: null,
    payment_method: 'free', upi_id: '', upi_qr_url: '', payment_instructions: '',
    banner_url: '', banner_focus_x: 50, banner_focus_y: 50, description: '', google_maps_link: ''
  });

  const onChange = (e) => setF({ ...f, [e.target.name]: e.target.value });
  const onBannerChange = (url) => setF({ ...f, banner_url: url });
  const onFocusChange = (fx, fy) => setF((prev) => ({ ...prev, banner_focus_x: fx, banner_focus_y: fy }));

  const submit = async (e) => {
    e.preventDefault();
    if (f.age_categories.length === 0) { setError('Select at least one age category'); return; }
    if (f.gender_categories.length === 0) { setError('Select at least one gender category'); return; }
    if (!f.registration_opens_at || !f.registration_closes_at || !f.starts_at || !f.ends_at) {
      setError('Please pick all four tournament dates'); return;
    }
    if (f.fee_mode === 'per_category') {
      const missing = [];
      const fees = f.category_fees || {};
      f.age_categories.forEach((a) => f.gender_categories.forEach((g) => {
        if (fees[`${a}|${g}`] === undefined || fees[`${a}|${g}`] === null) missing.push(`${a} ${g}`);
      }));
      if (missing.length) { setError(`Set fees for: ${missing.join(', ')}`); return; }
    }
    if (f.payment_method === 'upi') {
      const anyPaid = f.fee_mode === 'per_category'
        ? Object.values(f.category_fees || {}).some((v) => Number(v) > 0)
        : Number(f.registration_fee) > 0;
      if (!anyPaid) { setError('Set a fee greater than 0 for at least one category'); return; }
      if (!f.upi_id) { setError('Enter your UPI ID for paid tournaments'); return; }
      if (!f.upi_qr_url) { setError('Upload your UPI QR code for paid tournaments'); return; }
    }
    setError(''); setLoading(true);
    try {
      await axios.post(`${API}/tournaments`, f, { withCredentials: true });
      navigate('/organizer');
    } catch (err) { setError(err.response?.data?.detail || 'Failed to create tournament'); }
    finally { setLoading(false); }
  };

  return (
    <>
      <SEO title="Create Tournament" description="List a new basketball tournament on Beyond Ball India." path="/organizer/create-tournament" />
      <div className="pt-24 pb-16 min-h-screen">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <button onClick={() => navigate('/organizer')} className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[#F97316] mb-6" data-testid="back-to-dashboard-link">
            <ArrowLeft size={16} weight="bold" /> Back to dashboard
          </button>

          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 mb-2" data-testid="create-tournament-title">Create tournament</h1>
          <p className="text-gray-600 mb-6">Reach players across India. Admin reviews within 24h.</p>

          {error && <div className="bg-red-50 border border-red-100 text-red-700 px-4 py-3 mb-5 text-sm rounded-xl" data-testid="create-error">{error}</div>}

          <form onSubmit={submit} className="bg-white border border-gray-200 rounded-2xl p-6 space-y-5">
            <TournamentBasicsSection f={f} onChange={onChange} setF={setF} />
            <TournamentLocationSection f={f} onChange={onChange} />
            <TournamentScheduleSection f={f} setF={setF} />
            <TournamentPaymentSection f={f} onChange={onChange} setF={setF} />
            <TournamentMediaSection f={f} onChange={onChange} onBannerChange={onBannerChange} onFocusChange={onFocusChange} />
            <TournamentContentSection f={f} onChange={onChange} />

            <button type="submit" disabled={loading} className="btn-primary w-full px-5 py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50" data-testid="submit-tournament-btn">
              {loading ? 'Creating...' : 'Create tournament'} {!loading && <Trophy size={16} weight="bold" />}
            </button>
            <p className="text-center text-xs text-gray-500">Reviewed by admin before going live.</p>
          </form>
        </div>
      </div>
    </>
  );
};

export default CreateTournament;
